import { Page, Tabs } from '@dixa/lib-shared-habla';
import { useMemo } from 'react';

import { FeatureFlagName } from 'utils/featureFlagging/featureFlags';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { kebabToCamelCase } from 'utils/formatting/string';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { preferencesFormsRoutes } from './views/preferencesForms.routes';

type TabIDs = 'sound' | 'keyboard-shortcuts' | 'summaries' | 'general';
type Tab = {
  readonly id: TabIDs;
  readonly label: string;
  readonly featureFlag?: FeatureFlagName;
};

const settingsPreferencesTabs: readonly Tab[] = [
  {
    id: 'sound',
    label: 'Sound',
  },
  {
    id: 'keyboard-shortcuts',
    label: 'Keyboard shortcuts',
  },
  {
    id: 'summaries',
    label: 'Summaries',
    featureFlag: 'agent-copilot',
  },
  {
    id: 'general',
    label: 'General',
  },
] as const;

type PreferencesTab = (typeof settingsPreferencesTabs)[number]['id'];

export const SettingsPreferencesView = (): JSX.Element => {
  const navigate = useAppNavigate();
  const featureFlags = useFeatureFlag(['agent-copilot']);

  const handleTabChange = (value: string) => {
    const tab = value as (typeof settingsPreferencesTabs)[number]['id'];
    navigate({
      path: `/settings/preferences/${tab}`,
    });
  };

  const selectedTab = (location.pathname?.split('/').pop() ??
    settingsPreferencesTabs[0]) as PreferencesTab;

  const guardedTabs: Tab[] = useMemo(() => {
    return settingsPreferencesTabs.filter((tab) => {
      if (tab.featureFlag) {
        return featureFlags[kebabToCamelCase(tab.featureFlag)];
      }
      return true;
    });
  }, [featureFlags]);

  return (
    <Page>
      <Page.Head title="Preferences" />

      <Page.Tabs>
        <Tabs
          tabs={guardedTabs}
          selectedId={selectedTab}
          onChange={handleTabChange}
        />
      </Page.Tabs>

      <Page.Body>{preferencesFormsRoutes}</Page.Body>
    </Page>
  );
};
